import { GetBusinessEstimateAnalyticsResponse } from '@lune-fe/dapi-models'
import {
    businessCategoryToChartColour,
    businessCategoryToChartLabels,
    businessCategoryToIcons,
    BusinessEmissionsCategories,
    EmissionsChartSection,
} from '@lune-fe/lune-components-lib'
import { formatNumbers, Text } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import { useMemo } from 'react'
import { Range } from 'react-date-range'

import EmissionsBreakdownTable, { EmissionsBreakdownItem } from 'components/EmissionsBreakdownTable'
import WhiteBoxContainer from 'components/WhiteBoxContainer'
import WhiteBoxContainerWithContent from 'components/WhiteBoxContainerWithContent'

const categoryKeys = Object.keys(businessCategoryToChartLabels) as BusinessEmissionsCategories[]

const BusinessEmissionsSections = ({
    data,
    dateRange,
}: {
    data?: GetBusinessEstimateAnalyticsResponse
    dateRange: Range
}) => {
    // Calculate category totals for breakdown table and ordering
    const { categoryTotals, categoryTotalsMap, totalEmissions } = useMemo(() => {
        if (!data?.timeseries || data.timeseries.length === 0) {
            return {
                categoryTotals: [],
                categoryTotalsMap: {} as Record<BusinessEmissionsCategories, number>,
            }
        }

        // Initialize all categories to 0
        const initialTotals = categoryKeys.reduce(
            (acc, key) => {
                acc[key] = 0
                return acc
            },
            {} as Record<BusinessEmissionsCategories, number>,
        )

        // Sum up totals from all days
        const totals = data.timeseries.reduce((acc, day) => {
            categoryKeys.forEach((key) => {
                acc[key] += day[key]
            })
            return acc
        }, initialTotals)

        const totalValue = Object.values(totals).reduce((sum, val) => sum + val, 0)

        const items: EmissionsBreakdownItem[] = Object.entries(totals)
            .map(([key, value]) => {
                const category = key as BusinessEmissionsCategories
                const IconComponent = businessCategoryToIcons[category]
                return {
                    label: businessCategoryToChartLabels[category],
                    value,
                    percentage: totalValue > 0 ? (value / totalValue) * 100 : 0,
                    color: businessCategoryToChartColour[category],
                    icon: <IconComponent />,
                }
            })
            .filter((item) => item.value > 0)
            .sort((a, b) => b.value - a.value)

        return { categoryTotals: items, categoryTotalsMap: totals, totalEmissions: totalValue }
    }, [data])

    // Transform timeseries for chart
    const chartData = useMemo(() => {
        if (!data?.timeseries) {
            return undefined
        }

        return data.timeseries.map((day) => {
            const categoryData = categoryKeys.reduce(
                (acc, key) => {
                    acc[key] = day[key]
                    return acc
                },
                {} as Record<BusinessEmissionsCategories, number>,
            )
            return {
                date: day.date,
                ...categoryData,
            }
        })
    }, [data])

    return (
        <>
            <WhiteBoxContainer
                sx={{
                    mb: 3 / 2,
                    p: 3,
                }}
            >
                <Text variant={'body2'} color={'TextSecondary'} sx={{ mb: 1 }}>
                    Your transactions have emitted
                </Text>
                <Text variant={'h4'}>{formatNumbers(totalEmissions ?? 0, 2)} tCO₂e</Text>
                <Box sx={{ mt: 2 }}>
                    <EmissionsChartSection
                        loading={false}
                        data={chartData ?? []}
                        dataColours={businessCategoryToChartColour}
                        dataLabels={businessCategoryToChartLabels}
                        dateRange={dateRange}
                        showLegend={true}
                        skipZeroOnTooltip={true}
                        isEmpty={totalEmissions === 0 || chartData?.length === 0}
                        yAxisLabel="tCO₂e"
                        labelOrdering={(
                            a: BusinessEmissionsCategories,
                            b: BusinessEmissionsCategories,
                        ) => {
                            // Order by total value descending (highest first)
                            const aValue = categoryTotalsMap[a] || 0
                            const bValue = categoryTotalsMap[b] || 0
                            return bValue - aValue
                        }}
                    />
                </Box>
            </WhiteBoxContainer>
            <WhiteBoxContainerWithContent
                title={'Emissions by category'}
                sx={{
                    width: '100%',
                    mb: 3 / 2,
                    '> .MuiBox-root:first-of-type': {
                        p: 3,
                    },
                    '> .MuiBox-root:last-of-type': {
                        p: 0,
                    },
                }}
            >
                <EmissionsBreakdownTable
                    items={categoryTotals}
                    headerLeftLabel="Category"
                    headerRightLabel="tCO₂e"
                />
            </WhiteBoxContainerWithContent>
        </>
    )
}

export default BusinessEmissionsSections
